import type { ReactNode } from 'react';
import { Check, Circle, ArrowRight } from 'lucide-react';
import type { GridColumn } from '../types';
import type { StageChecklistItem } from './ProjectForm';
import { LIFECYCLE_STAGES, RANK } from '../utils/stages';
import { useT } from '../i18n';

// The project's process, in one place: where it stands in the lifecycle and
// what the next stage needs. Clicking a stage opens its "À faire" view (the
// fields that stage asks for, as steps). Everything that moves a project
// between stages (advance, préqualification decision, close) sits in the
// band below the steps — and again at the end of the current stage's view.
// Admins set a stage by hand from the project settings.

/** Pipeline label → i18n key of its display name. */
export const STAGE_NAME_KEY: Record<string, string> = {
  Inbox: 'projectDetail.stage.inbox',
  'Préqualification': 'projectDetail.stage.prequalification',
  Scoping: 'projectDetail.stage.scoping',
  Qualification: 'projectDetail.stage.qualification',
  Planning: 'projectDetail.stage.planning',
  Execution: 'projectDetail.stage.execution',
  Handover: 'projectDetail.stage.handover',
  Closed: 'projectDetail.stage.closed',
  Deferred: 'projectDetail.stage.deferred',
  Backlog: 'projectDetail.stage.backlog',
};

export function useStageName() {
  const { t } = useT();
  return (label: string) => (STAGE_NAME_KEY[label] ? t(STAGE_NAME_KEY[label]) : label);
}

const SIDE_STATES = ['Deferred', 'Backlog'];

interface NextProps {
  currentLabel: string;
  rank: number;
  checklist: StageChecklistItem[];
  milestonesDone: number;
  milestonesTotal: number;
  canEdit: boolean;
  onJumpItem: (item: StageChecklistItem) => void;
  onViewStage: (label: string) => void;
  onAdvance: (label: string) => void;
  onDecide: (verdict: 'advance' | 'defer') => void;
  onClose: () => void;
}

/** What the next stage needs and the action that moves the project: title, help, checklist, buttons. */
function useStageNext({ currentLabel, rank, checklist, milestonesDone, milestonesTotal, canEdit, onJumpItem, onViewStage, onAdvance, onDecide, onClose }: NextProps) {
  const { t } = useT();
  const stageName = useStageName();
  const isSide = SIDE_STATES.includes(currentLabel);

  // Execution has no field requirement, but the Handover means every milestone is done.
  const items: StageChecklistItem[] = currentLabel === 'Execution'
    ? [{ key: 'milestonesDone', labelKey: '', ok: milestonesTotal > 0 && milestonesDone === milestonesTotal, topic: 'plan' }]
    : currentLabel === 'Préqualification' ? [] // a decision, not a checklist: the two buttons say it
    : checklist;
  const itemLabel = (i: StageChecklistItem) => i.key === 'milestonesDone'
    ? t('projectDetail.stageBar.milestonesDone', { done: milestonesDone, total: milestonesTotal })
    : t(i.labelKey);
  const ready = items.every(i => i.ok);
  const nextLabel = !isSide && rank < RANK.closed ? LIFECYCLE_STAGES[rank + 1] : null;

  const checklistView = items.length > 0 && (
    <ul className="stage-checklist">
      {items.map(i => (
        <li key={i.key} className={i.ok ? 'is-ok' : 'is-missing'}>
          <button type="button" onClick={() => onJumpItem(i)} title={t('projectDetail.stageBar.jumpTo')}>
            {i.ok ? <Check size={13} strokeWidth={2.5} /> : <Circle size={11} />}
            <span>{itemLabel(i)}</span>
          </button>
        </li>
      ))}
    </ul>
  );

  let title = '';
  let help: string | null = null;
  let actions: ReactNode = null;
  switch (currentLabel) {
    case 'Inbox':
    case 'Scoping':
    case 'Planning':
    case 'Execution':
      title = t('projectDetail.stageBar.toNext', { stage: stageName(nextLabel ?? '') });
      if (canEdit && nextLabel) {
        actions = (
          <button type="button" className="btn btn-accent" disabled={!ready} onClick={() => onAdvance(nextLabel)}
            title={ready ? undefined : t('projectDetail.stageBar.completeFirst')}>
            {t('projectDetail.stageBar.advanceTo', { stage: stageName(nextLabel) })} <ArrowRight size={13} />
          </button>
        );
      }
      if (currentLabel === 'Execution') help = t('projectDetail.stageBar.executionHelp');
      break;
    case 'Préqualification':
      title = t('projectDetail.stageBar.prequalTitle');
      help = t('projectDetail.stageBar.prequalHelp');
      if (canEdit) {
        actions = (<>
          <button type="button" className="btn btn-ghost" onClick={() => onDecide('defer')}>{t('projectDetail.prequalDecision.deferButton')}</button>
          <button type="button" className="btn btn-accent" onClick={() => onDecide('advance')}>
            {t('projectDetail.stageBar.advanceTo', { stage: stageName('Scoping') })} <ArrowRight size={13} />
          </button>
        </>);
      }
      break;
    case 'Qualification':
      title = t('projectDetail.stageBar.qualificationTitle');
      help = t('projectDetail.stageBar.qualificationHelp');
      if (canEdit) actions = <a className="btn btn-ghost" href="#council">{t('projectDetail.stageBar.openCouncil')}</a>;
      break;
    case 'Handover':
      title = t('projectDetail.stageBar.handoverTitle');
      help = t('projectDetail.stageBar.handoverHelp');
      if (canEdit) {
        actions = (<>
          <button type="button" className="btn btn-ghost" onClick={() => onViewStage('Handover')}>{t('projectDetail.stageBar.openResults')}</button>
          <button type="button" className="btn btn-accent" onClick={onClose}>{t('projectDetail.stageBar.recordAndClose')} <ArrowRight size={13} /></button>
        </>);
      }
      break;
    case 'Closed':
      title = t('projectDetail.stageBar.closedTitle');
      break;
    case 'Deferred':
      title = t('projectDetail.stageBar.deferredTitle');
      help = t('projectDetail.stageBar.deferredHelp');
      break;
    case 'Backlog':
      title = t('projectDetail.stageBar.backlogTitle');
      help = t('projectDetail.stageBar.backlogHelp');
      break;
    default:
      title = currentLabel;
  }
  return { title, help, checklistView, actions };
}

/** The decision that ends a stage's "À faire" view: the same title, checklist and buttons as the header band. */
// (Inside the card the title and help are left out: the card's own title and
// the view's intro already say them.)
export function StageNext(props: NextProps) {
  const { checklistView, actions } = useStageNext(props);
  return (
    <div className="stage-next stage-next--card">
      {checklistView && <div className="stage-next-body">{checklistView}</div>}
      {actions && <div className="stage-next-actions">{actions}</div>}
    </div>
  );
}

interface Props extends NextProps {
  pipelineColumns: GridColumn[];
  /** The stage whose "À faire" view is open below, if any — underlined in the steps. */
  viewedLabel: string | null;
}

export function ProjectStageBar(props: Props) {
  const { pipelineColumns, currentLabel, rank, viewedLabel, onViewStage } = props;
  const { t } = useT();
  const stageName = useStageName();
  const exists = (label: string) => pipelineColumns.some(c => c.label === label);
  const isSide = SIDE_STATES.includes(currentLabel);
  const steps = LIFECYCLE_STAGES.filter(exists);
  const { title, help, checklistView, actions } = useStageNext(props);

  return (
    <div className="stage-bar">
      <div className="stage-steps-row">
        <ol className="stage-steps" aria-label={t('projectDetail.stageBar.ariaSteps')}>
          {steps.map((label, i) => {
            const state = isSide
              ? (i <= rank ? 'is-done' : 'is-todo')
              : (i < rank || currentLabel === 'Closed' ? 'is-done' : i === rank ? 'is-current' : 'is-todo');
            return (
              <li key={label} className={`stage-step ${state}${viewedLabel === label ? ' is-viewed' : ''}`}>
                <button type="button" className="stage-step-inner" onClick={() => onViewStage(label)}
                  aria-current={label === currentLabel ? 'step' : undefined}
                  title={t('projectDetail.stageBar.viewStage', { stage: stageName(label) })}>
                  <span className="stage-dot">{state === 'is-done' ? <Check size={10} strokeWidth={3} /> : null}</span>
                  <span className="stage-name">{stageName(label)}</span>
                </button>
              </li>
            );
          })}
        </ol>
        {isSide && (
          <div className="stage-sides">
            <span className="badge badge-warn">{stageName(currentLabel)}</span>
          </div>
        )}
      </div>
      <div className="stage-next">
        <div className="stage-next-body">
          <div className="stage-next-title">{title}</div>
          {help && <div className="stage-next-help">{help}</div>}
          {checklistView}
        </div>
        {actions && <div className="stage-next-actions">{actions}</div>}
      </div>
    </div>
  );
}
